import { BU } from '../engine/gpu-flags.js';
import { createMappedBuffer } from '../resource/gpu-buffers.js';
import { ObservableVec3 } from '../math/observable-vec3.js';
import { ObservableQuat } from '../math/observable-quat.js';
import { createWorldMatrixState, attachWorldMatrixState, composeTrsLocalMatrix } from '../scene/world-matrix-state.js';
import { createEulerProxy } from '../scene/scene-node.js';
import { eulerToQuat } from '../math/quat-euler.js';

function initMeshTransform(partialMesh, px = 0, py = 0, pz = 0, rx = 0, ry = 0, rz = 0, sx = 1, sy = 1, sz = 1) {
  const wm = createWorldMatrixState(() => composeTrsLocalMatrix(mesh.position, mesh.rotationQuaternion, mesh.scaling));
  const onWmDirty = () => wm.markLocalDirty();
  const [iqx, iqy, iqz, iqw] = eulerToQuat(rx, ry, rz);
  const rq = new ObservableQuat(iqx, iqy, iqz, iqw, onWmDirty);
  const rotationQuaternion = rq;
  const rotation = createEulerProxy(rq);
  const position = new ObservableVec3(px, py, pz, onWmDirty);
  const scaling = new ObservableVec3(sx, sy, sz, onWmDirty);
  const mesh = { ...partialMesh, position, rotationQuaternion, rotation, scaling };
  if (!mesh.children) {
    mesh.children = [];
  }
  Object.defineProperty(mesh, "parent", {
    get() {
      return wm.parent;
    },
    set(v) {
      wm.parent = v;
    },
    configurable: true,
    enumerable: true
  });
  Object.defineProperty(mesh, "worldMatrix", {
    get() {
      return wm.getWorldMatrix();
    },
    configurable: true,
    enumerable: false
  });
  Object.defineProperty(mesh, "worldMatrixVersion", {
    get() {
      return wm.getWorldMatrixVersion();
    },
    configurable: true,
    enumerable: false
  });
  attachWorldMatrixState(mesh, wm);
  return mesh;
}
function uploadMeshToGPU(engine, positions, normals, indices, uvs, uvs2, tangents, colors) {
  const device = engine._device;
  const positionBuffer = createMappedBuffer(engine, positions, BU.VERTEX);
  const normalBuffer = createMappedBuffer(engine, normals, BU.VERTEX);
  const indexBuffer = createMappedBuffer(engine, indices, BU.INDEX);
  let uvBuffer;
  if (uvs && uvs.length > 0) {
    uvBuffer = createMappedBuffer(engine, uvs, BU.VERTEX);
  } else {
    uvBuffer = device.createBuffer({
      size: positions.length / 3 * 8,
      usage: BU.VERTEX,
      mappedAtCreation: true
    });
    uvBuffer.unmap();
  }
  let uv2Buffer = null;
  if (uvs2 && uvs2.length > 0) {
    uv2Buffer = createMappedBuffer(engine, uvs2, BU.VERTEX);
  }
  const tangentBuffer = tangents && tangents.length > 0 ? createMappedBuffer(engine, tangents, BU.VERTEX) : null;
  const colorBuffer = colors && colors.length > 0 ? createMappedBuffer(engine, colors, BU.VERTEX) : null;
  return {
    positionBuffer,
    normalBuffer,
    uvBuffer,
    uv2Buffer,
    tangentBuffer,
    colorBuffer,
    hasUv: !!uvs && uvs.length > 0,
    hasUv2: !!uvs2 && uvs2.length > 0,
    hasTangent: !!tangents && tangents.length > 0,
    hasColor: !!colors && colors.length > 0,
    indexBuffer,
    indexCount: indices.length,
    indexFormat: "uint32"
  };
}

export { initMeshTransform, uploadMeshToGPU };
//# sourceMappingURL=mesh.js.map
